import { useState, useEffect, useCallback } from 'react'
import {
    Bar,
    BarChart,
    Line,
    LineChart,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts'
import { MdSubscriptions, MdOutlinePermMedia, MdOutlineImage, MdOutlineVideocam, MdOutlineAudiotrack } from 'react-icons/md'
import { FaTasks } from 'react-icons/fa'
import { apiFetch } from '@/lib/api'
import { formatTick, formatDuration, CHART_COLORS } from './utils'
import { StatCard, ChartPanel, ChartTooltip, Segmented } from './components'

const ENTITIES = [
    { value: 'episodes', label: 'Episodes' },
    { value: 'assets', label: 'Assets' },
    { value: 'tasks', label: 'Tasks' },
]

const ENTITY_LABEL = { episodes: 'Episodes', assets: 'Assets', tasks: 'Tasks' }

const TYPE_COLORS = {
    image: 'var(--chart-4)',
    video: 'var(--chart-2)',
    audio: 'var(--chart-1)',
    other: 'var(--chart-5)',
}

const axisProps = {
    stroke: 'var(--secondary-foreground)',
    tick: { fontSize: 11, fill: 'var(--secondary-foreground)' },
    tickLine: false,
    axisLine: { stroke: 'var(--border)' },
}

const MODEL_AXIS_WIDTH = 140

// Single-line, ellipsis-truncated Y-axis label for model names.
// Uses a foreignObject so we get CSS truncation plus a native hover
// tooltip (title) exposing the full model name.
function ModelTick({ x, y, payload, width = MODEL_AXIS_WIDTH }) {
    const value = payload?.value ?? ''
    return (
        <foreignObject x={x - width} y={y - 8} width={width} height={16}>
            <div
                title={value}
                style={{
                    width: '100%',
                    fontSize: 10,
                    lineHeight: '16px',
                    color: 'var(--secondary-foreground)',
                    textAlign: 'right',
                    paddingRight: 6,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {value}
            </div>
        </foreignObject>
    )
}

export default function ContentTab({ showId, range, tz }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [entity, setEntity] = useState('tasks')

    const fetchData = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const params = new URLSearchParams({ range, tz })
            const res = await apiFetch(`/api/shows/${showId}/statistics/content?${params.toString()}`)
            if (!res.ok) throw new Error('Failed to load content statistics')
            setData(await res.json())
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [showId, range, tz])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const resolution = data?.resolution || 'day'
    const series = data?.series || []
    const taskStatus = data?.taskStatus || []
    const ratio = data?.taskRatio || { completed: 0, failed: 0, total: 0 }
    const typeDistribution = data?.typeDistribution || []
    const modelUsage = data?.modelUsage || []
    const modelRuntimeAvg = data?.modelRuntimeAvg || []
    const successRate = ratio.total ? Math.round((ratio.completed / ratio.total) * 100) : 0

    const ratioData = [
        { name: 'Completed', value: ratio.completed, color: 'var(--chart-1)' },
        { name: 'Failed', value: ratio.failed, color: 'var(--destructive-foreground)' },
    ]

    const hasNewInRange = series.some((p) => p[`${entity}New`] > 0)
    const hasTaskActivity = ratio.total > 0
    const hasTypes = typeDistribution.some((t) => t.count > 0)
    const hasModels = modelUsage.length > 0
    const hasRuntime = modelRuntimeAvg.length > 0

    if (loading) {
        return <div className='py-20 text-center text-sm text-secondary-foreground/60'>Loading statistics...</div>
    }
    if (error) {
        return <div className='py-20 text-center text-sm text-destructive-foreground'>{error}</div>
    }

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
                <span className='text-xs uppercase tracking-wide text-secondary-foreground/50'>Totals in selected period</span>
                <div className='flex flex-wrap gap-4'>
                    <StatCard label='New Episodes' value={data?.totals?.episodes} icon={<MdSubscriptions className='w-5 h-5' />} accent='var(--chart-2)' />
                    <StatCard label='New Assets' value={data?.totals?.assets} icon={<MdOutlinePermMedia className='w-5 h-5' />} accent='var(--chart-4)' />
                    <StatCard label='New Images' value={data?.totals?.assetImages} icon={<MdOutlineImage className='w-5 h-5' />} accent='var(--chart-4)' />
                    <StatCard label='New Videos' value={data?.totals?.assetVideos} icon={<MdOutlineVideocam className='w-5 h-5' />} accent='var(--chart-2)' />
                    <StatCard label='New Audio' value={data?.totals?.assetAudio} icon={<MdOutlineAudiotrack className='w-5 h-5' />} accent='var(--chart-1)' />
                    <StatCard label='Tasks' value={data?.totals?.tasks} icon={<FaTasks className='w-5 h-5' />} accent='var(--chart-1)' />
                </div>
            </div>

            <ChartPanel
                title='Growth over time'
                action={<Segmented size='sm' options={ENTITIES} value={entity} onChange={setEntity} />}
            >
                {!hasNewInRange && (
                    <p className='text-xs text-secondary-foreground/60'>
                        No new {ENTITY_LABEL[entity].toLowerCase()} in this period. Try a wider range.
                    </p>
                )}
                <div className='h-72 w-full'>
                    <ResponsiveContainer width='100%' height='100%'>
                        <BarChart data={series} syncId='timeline' syncMethod='value' margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                            <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' vertical={false} />
                            <XAxis dataKey='ts' tickFormatter={(ts) => formatTick(ts, resolution)} {...axisProps} />
                            <YAxis allowDecimals={false} {...axisProps} />
                            <Tooltip
                                content={
                                    <ChartTooltip
                                        resolution={resolution}
                                        nameMap={{ [`${entity}New`]: `New ${ENTITY_LABEL[entity]}` }}
                                    />
                                }
                                cursor={{ fill: 'var(--muted)', opacity: 0.3 }}
                            />
                            <Bar
                                dataKey={`${entity}New`}
                                name={`New ${ENTITY_LABEL[entity]}`}
                                fill='var(--chart-2)'
                                radius={[3, 3, 0, 0]}
                                maxBarSize={28}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </ChartPanel>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                <ChartPanel title='Tasks: completed vs failed'>
                    {!hasTaskActivity && (
                        <p className='text-xs text-secondary-foreground/60'>No completed or failed tasks in this period.</p>
                    )}
                    <div className='h-64 w-full'>
                        <ResponsiveContainer width='100%' height='100%'>
                            <BarChart data={taskStatus} syncId='timeline' syncMethod='value' margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                                <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' vertical={false} />
                                <XAxis dataKey='ts' tickFormatter={(ts) => formatTick(ts, resolution)} {...axisProps} />
                                <YAxis allowDecimals={false} {...axisProps} />
                                <Tooltip
                                    content={<ChartTooltip resolution={resolution} />}
                                    cursor={{ fill: 'var(--muted)', opacity: 0.3 }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey='completed' name='Completed' stackId='s' fill='var(--chart-1)' radius={[0, 0, 0, 0]} maxBarSize={28} />
                                <Bar dataKey='failed' name='Failed' stackId='s' fill='var(--destructive-foreground)' radius={[3, 3, 0, 0]} maxBarSize={28} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartPanel>

                <ChartPanel title='Error rate over time'>
                    {!hasTaskActivity && (
                        <p className='text-xs text-secondary-foreground/60'>No completed or failed tasks in this period.</p>
                    )}
                    <div className='h-64 w-full'>
                        <ResponsiveContainer width='100%' height='100%'>
                            <LineChart data={taskStatus} syncId='timeline' syncMethod='value' margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                                <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' vertical={false} />
                                <XAxis dataKey='ts' tickFormatter={(ts) => formatTick(ts, resolution)} {...axisProps} />
                                <YAxis
                                    domain={[0, 100]}
                                    tickFormatter={(v) => `${v}%`}
                                    {...axisProps}
                                />
                                <Tooltip
                                    content={
                                        <ChartTooltip
                                            resolution={resolution}
                                            nameMap={{ errorRate: 'Error rate' }}
                                            valueFormatter={(v) => `${v}%`}
                                        />
                                    }
                                    cursor={{ stroke: 'var(--muted)' }}
                                />
                                <Line
                                    type='monotone'
                                    dataKey='errorRate'
                                    name='Error rate'
                                    stroke='var(--destructive-foreground)'
                                    strokeWidth={2}
                                    dot={false}
                                    connectNulls
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </ChartPanel>

                <ChartPanel title='Usage by model'>
                    {!hasModels ? (
                        <div className='py-16 text-center text-sm text-secondary-foreground/60'>No model usage in this period.</div>
                    ) : (
                        <div className='h-64 w-full'>
                            <ResponsiveContainer width='100%' height='100%'>
                                <BarChart
                                    data={modelUsage}
                                    layout='vertical'
                                    margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' horizontal={false} />
                                    <XAxis type='number' allowDecimals={false} {...axisProps} />
                                    <YAxis
                                        type='category'
                                        dataKey='model'
                                        width={MODEL_AXIS_WIDTH}
                                        interval={0}
                                        {...axisProps}
                                        tick={<ModelTick />}
                                    />
                                    <Tooltip
                                        content={<ChartTooltip labelKey='model' nameMap={{ count: 'Tasks' }} />}
                                        cursor={{ fill: 'var(--muted)', opacity: 0.3 }}
                                    />
                                    <Bar dataKey='count' name='Tasks' fill='var(--chart-4)' radius={[0, 3, 3, 0]} maxBarSize={22} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </ChartPanel>

                <ChartPanel title='Median run time by model'>
                    {!hasRuntime ? (
                        <div className='py-16 text-center text-sm text-secondary-foreground/60'>
                            No completed tasks in this period.
                        </div>
                    ) : (
                        <div className='h-64 w-full'>
                            <ResponsiveContainer width='100%' height='100%'>
                                <BarChart
                                    data={modelRuntimeAvg}
                                    layout='vertical'
                                    margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' horizontal={false} />
                                    <XAxis
                                        type='number'
                                        allowDecimals={false}
                                        tickFormatter={(v) => formatDuration(v)}
                                        {...axisProps}
                                    />
                                    <YAxis
                                        type='category'
                                        dataKey='model'
                                        width={MODEL_AXIS_WIDTH}
                                        interval={0}
                                        {...axisProps}
                                        tick={<ModelTick />}
                                    />
                                    <Tooltip
                                        content={
                                            <ChartTooltip
                                                labelKey='model'
                                                nameMap={{ medianSeconds: 'Median run time' }}
                                                valueFormatter={(v, p) =>
                                                    `${formatDuration(v)} · p95 ${formatDuration(p?.p95Seconds ?? 0)} (${(p?.count ?? 0).toLocaleString()} tasks)`
                                                }
                                            />
                                        }
                                        cursor={{ fill: 'var(--muted)', opacity: 0.3 }}
                                    />
                                    <Bar dataKey='medianSeconds' name='Median run time' fill='var(--chart-2)' radius={[0, 3, 3, 0]} maxBarSize={22} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </ChartPanel>

                <ChartPanel title='Completed vs failed ratio'>
                    <div className='flex flex-col items-center'>
                        <div className='h-44 w-full relative'>
                            <ResponsiveContainer width='100%' height='100%'>
                                <PieChart>
                                    <Pie
                                        data={ratioData}
                                        dataKey='value'
                                        nameKey='name'
                                        innerRadius={50}
                                        outerRadius={70}
                                        paddingAngle={2}
                                        stroke='none'
                                    >
                                        {ratioData.map((d) => (
                                            <Cell key={d.name} fill={d.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<ChartTooltip hideLabel />} wrapperStyle={{ zIndex: 20 }} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className='absolute inset-0 z-0 flex flex-col items-center justify-center pointer-events-none'>
                                <span className='text-2xl font-bold tabular-nums'>{successRate}%</span>
                                <span className='text-[10px] uppercase text-secondary-foreground/60'>success</span>
                            </div>
                        </div>
                        <div className='mt-2 flex w-full justify-around text-sm'>
                            <div className='flex flex-col items-center'>
                                <span className='flex items-center gap-1.5 text-secondary-foreground'>
                                    <span className='h-2 w-2 rounded-full' style={{ background: 'var(--chart-1)' }} />
                                    Completed
                                </span>
                                <span className='font-bold tabular-nums'>{ratio.completed.toLocaleString()}</span>
                            </div>
                            <div className='flex flex-col items-center'>
                                <span className='flex items-center gap-1.5 text-secondary-foreground'>
                                    <span className='h-2 w-2 rounded-full' style={{ background: 'var(--destructive-foreground)' }} />
                                    Failed
                                </span>
                                <span className='font-bold tabular-nums'>{ratio.failed.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                </ChartPanel>

                <ChartPanel title='Distribution by type'>
                    {!hasTypes ? (
                        <div className='py-16 text-center text-sm text-secondary-foreground/60'>No task activity in this period.</div>
                    ) : (
                        <div className='flex flex-col items-center'>
                            <div className='h-44 w-full'>
                                <ResponsiveContainer width='100%' height='100%'>
                                    <PieChart>
                                        <Pie
                                            data={typeDistribution}
                                            dataKey='count'
                                            nameKey='type'
                                            innerRadius={50}
                                            outerRadius={70}
                                            paddingAngle={2}
                                            stroke='none'
                                        >
                                            {typeDistribution.map((d, i) => (
                                                <Cell key={d.type} fill={TYPE_COLORS[d.type] || CHART_COLORS[i % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<ChartTooltip hideLabel />} wrapperStyle={{ zIndex: 20 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className='mt-2 flex w-full flex-wrap justify-center gap-x-5 gap-y-1 text-sm'>
                                {typeDistribution.map((d, i) => (
                                    <div key={d.type} className='flex items-center gap-1.5 text-secondary-foreground'>
                                        <span
                                            className='h-2 w-2 rounded-full'
                                            style={{ background: TYPE_COLORS[d.type] || CHART_COLORS[i % CHART_COLORS.length] }}
                                        />
                                        <span className='capitalize'>{d.type}</span>
                                        <span className='font-bold tabular-nums'>{d.count.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </ChartPanel>
            </div>
        </div>
    )
}
